import React from 'react';
import { 
  FaBed, FaBath, FaCar, FaRulerCombined, FaSwimmingPool, 
  FaWifi, FaSnowflake, FaTv, FaUmbrellaBeach, FaCoffee, 
  FaDumbbell, FaParking, FaDog, FaBuilding, FaThermometerHalf,
  FaMapMarkerAlt, FaExternalLinkAlt, FaPhoneAlt, FaEnvelope, 
  FaHandshake, FaLayerGroup, FaCalendarAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const ProjectSidebar = ({ project, t }) => {
  // Fonction pour obtenir les coordonnées GPS en fonction de l'ID du projet
  const getCoordinates = () => {
    switch (project.id) {
      case 1:
        return '14.744503310033656,-17.505136969224704';
      case 2:
        return '14.744503310033656,-17.505136969224704';
      case 3:
        return '14.744503310033656,-17.505136969224704';
      case 4:
        return '14.821926910325793,-17.264508528587587';
      case 5:
        return '14.767307566576427,-17.207139865435206';
      case 6:
        return '14.748871549918656,-17.290075243974226';
      default:
        return '14.82257140017839,-17.249831923528305';
    }
  };

  // Mappage des icônes pour les caractéristiques
  const iconMap = {
    'chambres': <FaBed className="text-socabeg-blue mr-2" />,
    'sdb': <FaBath className="text-socabeg-blue mr-2" />,
    'parking': <FaCar className="text-socabeg-blue mr-2" />,
    'surface': <FaRulerCombined className="text-socabeg-blue mr-2" />,
    'piscine': <FaSwimmingPool className="text-socabeg-blue mr-2" />,
    'wifi': <FaWifi className="text-socabeg-blue mr-2" />,
    'climatisation': <FaSnowflake className="text-socabeg-blue mr-2" />,
    'tv': <FaTv className="text-socabeg-blue mr-2" />,
    'plage': <FaUmbrellaBeach className="text-socabeg-blue mr-2" />,
    'restauration': <FaCoffee className="text-socabeg-blue mr-2" />,
    'sport': <FaDumbbell className="text-socabeg-blue mr-2" />,
    'parking-prive': <FaParking className="text-socabeg-blue mr-2" />,
    'animaux': <FaDog className="text-socabeg-blue mr-2" />,
    'immeuble': <FaBuilding className="text-socabeg-blue mr-2" />,
    'chauffage': <FaThermometerHalf className="text-socabeg-blue mr-2" />,
    'etage': <FaLayerGroup className="text-socabeg-blue mr-2" />,
    'annee': <FaCalendarAlt className="text-socabeg-blue mr-2" />,
    'floor': <FaLayerGroup className="text-socabeg-blue mr-2" />
  };

  // Fonction pour obtenir l'icône correspondante
  const getFeatureIcon = (feature) => {
    const lowerFeature = feature.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerFeature.includes(key)) {
        return icon;
      }
    }
    return <FaRulerCombined className="text-socabeg-blue mr-2" />;
  };

  return (
    <div className="lg:w-96 flex-shrink-0 space-y-6">
      {/* Caractéristiques */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-socabeg-blue mb-6">
          {t('realEstateDetail.features')}
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {project.details && Object.entries(project.details).map(([key, value]) => (
            <div key={key} className="flex items-center">
              {getFeatureIcon(key)}
              <div>
                <p className="text-sm text-gray-500 capitalize">{t(key)}</p>
                <p className="font-medium">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Localisation */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-socabeg-blue mb-6">
          {t('realEstateDetail.location')}
        </h2>
        
        <div className="mb-6">
          <div className="flex items-start mb-4">
            <FaMapMarkerAlt className="text-socabeg-red text-xl mt-1 mr-3 flex-shrink-0" />
            <div>
              <p className="text-gray-800 font-medium">
                {project.localisation.localisation || project.localisation.adresse}
              </p>
              {project.localisation.adresse && project.localisation.localisation && (
                <p className="text-gray-600 text-sm mt-1">
                  {project.localisation.adresse}
                </p>
              )}
            </div>
          </div>

          {/* Carte Google Maps */}
          <div className="h-64 rounded-lg overflow-hidden mb-4">
            <iframe
              src={`https://maps.google.com/maps?q=${getCoordinates()}&z=15&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title={`Localisation ${project.title}`}
            ></iframe>
          </div>

          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${getCoordinates()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-socabeg-blue hover:text-socabeg-blue-dark transition-colors"
          >
            {t('realEstateDetail.viewOnGoogleMaps')}
            <FaExternalLinkAlt className="ml-2 text-xs" />
          </a>
        </div>

        {project.pointsOfInterest && project.pointsOfInterest.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              {t('realEstateDetail.nearbyPointsOfInterest')}
            </h3>
            <ul className="space-y-2">
              {project.pointsOfInterest.map((point, index) => (
                <li key={index} className="flex items-center">
                  <FaMapMarkerAlt className="text-socabeg-red mr-2 flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Formulaire de contact */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-socabeg-blue mb-6">
          {t('realEstateDetail.booking.title')}
        </h2>
        
        <div className="space-y-6">
          {/* Prix */}
          <div className="bg-socabeg-blue/5 p-4 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">{t('realEstateDetail.price')}</div>
            <div className="text-2xl font-bold text-socabeg-blue">
              {project.price || t('realEstateDetail.contactUs')}
            </div>
          </div>

          {/* Variantes de logements si disponibles */}
          {project.variants && project.variants.length > 0 && (
            <div className="mt-4 bg-white border border-gray-100 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Typologies disponibles
              </h3>
              <div className="space-y-2 text-sm">
                {project.variants.map((variant, index) => (
                  <div
                    key={index}
                    className="flex flex-col border border-gray-100 rounded-md p-2"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-gray-800">
                        {variant.name}
                      </span>
                      <span className="text-socabeg-blue font-semibold">
                        {variant.price}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-gray-600 text-xs">
                      <span>{variant.surface}</span>
                      <span>{variant.chambres} ch.</span>
                      <span>{variant.sdb} sdb</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton de contact */}
          <Link 
            to="/contact"
            className="w-full bg-socabeg-blue text-white py-3 px-6 rounded-lg font-medium hover:bg-socabeg-blue-dark transition-colors flex items-center justify-center"
          >
            <FaHandshake className="mr-2" />
            {t('realEstateDetail.contactUs')}
          </Link>

          {/* Informations de contact */}
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <h3 className="font-medium text-gray-800">{t('realEstateDetail.contactInfo')}</h3>
            
            <a 
              href="tel:+221338893535"
              className="flex items-center text-gray-700 hover:text-socabeg-blue transition-colors"
            >
              <FaPhoneAlt className="mr-3 text-socabeg-blue" />
              +221 33 889 35 35
            </a>
            
            <a 
              href="mailto:infos@socabeg.com"
              className="flex items-center text-gray-700 hover:text-socabeg-blue transition-colors"
            >
              <FaEnvelope className="mr-3 text-socabeg-blue" />
              infos@socabeg.com
            </a>
          </div>

          {/* Modalités de réservation */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="font-medium text-gray-800 mb-3">
              {t('realEstateDetail.booking.terms')}
            </h3>
            <div className="space-y-3">
              {project.reservationConditions ? (
                <div className="text-gray-700 text-sm">
                  {project.reservationConditions.split('\n').map((line, index) => (
                    <p key={index} className="mb-2">{line}</p>
                  ))}
                </div>
              ) : (
                <>
                  {t(`projects.${project.projectKey}.booking.fees`) && (
                    <div className="mb-2">
                      <p className="text-gray-700 text-sm">
                        {t(`projects.${project.projectKey}.booking.fees`)}
                      </p>
                    </div>
                  )}
                  <div className="mb-2">
                    <p className="text-gray-700 text-sm">
                      {t(`projects.${project.projectKey}.booking.deposit`)}
                    </p>
                  </div>
                  
                  {t(`projects.${project.projectKey}.booking.balance`) && (
                    <div className="mb-2">
                      <p className="text-gray-700 text-sm">
                        {t(`projects.${project.projectKey}.booking.balance`)}
                      </p>
                    </div>
                  )}
                  
                  <div className="mb-2">
                    <p className="text-gray-700 text-sm">
                      {t(`projects.${project.projectKey}.booking.financing`)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSidebar;
